import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Tag,
  Upload,
  Input,
  Popconfirm,
  Collapse,
  message,
  Select,
  Descriptions,
  DatePicker,
  Modal
} from "antd";
import {
  DownloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CheckCircleFilled,
  UploadOutlined,
  WarningFilled,
  PlusOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined
} from "@ant-design/icons";
import moment from "moment";
import OrderService from '../../utils/api/orders';
import ServiceStatus from "../Generals/ServiceStatus";
import { VSCategoriesData } from "../../data/vs";
import { OrderLogs } from "../../data/orderStatusLogs";
import CategoryTag from "../Tags";
import ServicesService from "../../utils/api/services";
import ServiceReviews from "./ServiceReviews";
import ServiceComplaints from "./ServiceComplaints";
import ChatService from "../../utils/api/chat";
import UserService from "../../utils/api/users";
import '../../scss/OrderInfoNew.scss';

const { Panel } = Collapse;

export default function NewOrderInfo(props) {
  const SHOW_QUOTE_TIMER_DEBUG = true;
  const { serviceData, client, reload, setReload, role } = props;
  const vsCategories = VSCategoriesData;
  const userData = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
  const effectiveRole = Number(role ?? userData?.role ?? localStorage.getItem('role'));
  const userRoleCat1 = userData?.role_data?.cat1;
  const canViewAssignedBuyerContact = effectiveRole === 1 || effectiveRole === 4 || effectiveRole === 5;
  const canViewAssignedSellerContact = effectiveRole === 1 || effectiveRole === 3;
  const [order] = useState(serviceData);
  const [assignedBuyerContact, setAssignedBuyerContact] = useState(null);
  const [assignedSellerContact, setAssignedSellerContact] = useState(null);

  useEffect(() => {
    let isMounted = true;

    if (!canViewAssignedBuyerContact && !canViewAssignedSellerContact) {
      setAssignedBuyerContact(null);
      setAssignedSellerContact(null);
      return;
    }

    const clientId = client?.id || order?.client_id || order?.clientId;

    if (!clientId) {
      setAssignedBuyerContact(null);
      setAssignedSellerContact(null);
      return;
    }

    UserService.GetAssignedPersonnel(clientId)
      .then((resp) => {
        if (!isMounted) return;

        const buyers = Array.isArray(resp?.data?.buyers) ? resp.data.buyers : [];
        const sellers = Array.isArray(resp?.data?.sellers) ? resp.data.sellers : [];
        const role3Buyer = buyers.find((buyer) => String(buyer?.role) === "3");
        const role5Seller = sellers.find((seller) => String(seller?.role) === "5");
        const selectedBuyer = role3Buyer || buyers[0] || null;
        const selectedSeller = role5Seller || sellers[0] || null;

        if (selectedBuyer) {
          setAssignedBuyerContact({
            name: selectedBuyer?.name || "No disponible",
            email: selectedBuyer?.email || "No disponible"
          });
        } else {
          setAssignedBuyerContact(null);
        }

        if (selectedSeller) {
          setAssignedSellerContact({
            name: selectedSeller?.name || "No disponible",
            email: selectedSeller?.email || "No disponible"
          });
        } else {
          setAssignedSellerContact(null);
        }
      })
      .catch(() => {
        if (isMounted) {
          setAssignedBuyerContact(null);
          setAssignedSellerContact(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [client?.id, order?.client_id, order?.clientId, canViewAssignedBuyerContact, canViewAssignedSellerContact]);

  const buyerContact = useMemo(() => {
    const buyerData =
      order?.buyer ||
      order?.buyerUser ||
      order?.buyer_user ||
      order?.assignedBuyer ||
      order?.assigned_buyer ||
      order?.createdBy ||
      order?.created_by ||
      null;

    const name =
      buyerData?.name ||
      order?.buyer_name ||
      order?.buyerName ||
      order?.assigned_buyer_name ||
      order?.assignedBuyerName ||
      order?.created_by_name ||
      order?.createdByName ||
      (effectiveRole === 3 ? userData?.name : "");

    const email =
      buyerData?.email ||
      order?.buyer_email ||
      order?.buyerEmail ||
      order?.assigned_buyer_email ||
      order?.assignedBuyerEmail ||
      order?.created_by_email ||
      order?.createdByEmail ||
      (effectiveRole === 3 ? userData?.email : "");

    return {
      name: assignedBuyerContact?.name || name || "No disponible",
      email: assignedBuyerContact?.email || email || "No disponible"
    };
  }, [order, effectiveRole, userData, assignedBuyerContact]);

  const sellerContact = useMemo(() => {
    const sellerData =
      order?.seller ||
      order?.sellerUser ||
      order?.seller_user ||
      order?.assignedSeller ||
      order?.assigned_seller ||
      order?.supplier ||
      order?.supplierUser ||
      order?.supplier_user ||
      null;

    const name =
      sellerData?.name ||
      sellerData?.bussiness ||
      order?.seller_name ||
      order?.sellerName ||
      order?.assigned_seller_name ||
      order?.assignedSellerName ||
      order?.supplier_name ||
      order?.supplierName ||
      order?.provider_name ||
      order?.providerName ||
      "";

    const email =
      sellerData?.email ||
      order?.seller_email ||
      order?.sellerEmail ||
      order?.assigned_seller_email ||
      order?.assignedSellerEmail ||
      order?.supplier_email ||
      order?.supplierEmail ||
      order?.provider_email ||
      order?.providerEmail ||
      "";

    return {
      name: assignedSellerContact?.name || name || "No disponible",
      email: assignedSellerContact?.email || email || "No disponible"
    };
  }, [order, assignedSellerContact]);
  const [loadServiceChangeStep, setLoadServiceChangeStep] = useState(false);
  const [filteredServices, setFilteredServices] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [positions, setPositions] = useState([]);
  const [proposalPrice, setProposalPrice] = useState([]);
  const [proposalFiles, setProposalFiles] = useState([]);
  const [application] = useState({
    title: '',
    description: '',
    price: 0,
    dueDate: '',
    type: '',
    fileList: [],
  });

  const [loadingQuote, setLoadingQuote] = useState(false);
  const [newQuote, setNewQuote] = useState({
    order_id: order.orderId,
    service_id: '',
    supplier_id: userData.id,
    supplier_name: "",
    supplier_bussiness: "",
    supplier_fcm: "n/a",
    title: "",
    description: "",
    price: 0,
    epno_price: 0,
    change_type: '',
    due_date: null,
    proposal_file: null,
    epno_file: null
  });

  const [newSupplierQuote, setNewSupplierQuote] = useState({
    order_id: order.orderId,
    service_id: '',
    supplier_id: role == 6 && userData.id,
    supplier_name: role == 6 && userData.name,
    supplier_bussiness: role == 6 && userData.bussiness,
    supplier_fcm: (role == 6 && userData.fcm) || '',
    title: "",
    description: "",
    price: 0,
    epno_price: 0,
    change_type: '',
    due_date: null,
    proposal_file: null,
    epno_file: null
  });

  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editValues, setEditValues] = useState({
    title: '',
    description: '',
    quantity: '',
    service_file: ''
  });

  const [editingProposal, setEditingProposal] = useState(null);
  const [editProposalValues, setEditProposalValues] = useState({
    title: '',
    description: '',
    price: 0,
    epno_price: 0,
    due_date: '',
    place: 0,
    change_type: ''
  });

  const [industryPO, setIndustryPO] = useState("");
  const [supplierPO, setSupplierPO] = useState("");
  const [truePriceByService, setTruePriceByService] = useState({});
  const [truePriceCurrencyByService, setTruePriceCurrencyByService] = useState({});
  const [truePriceLockedByService, setTruePriceLockedByService] = useState({});
  const [savingTruePriceByService, setSavingTruePriceByService] = useState({});
  const [reviews, setReviews] = useState({});
  const [complaints, setComplaints] = useState({});

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelType, setCancelType] = useState("");

  const [cancelServiceModal, setCancelServiceModal] = useState(false);
  const [selectedServiceToCancel, setSelectedServiceToCancel] = useState(null);
  const [cancelServiceReason, setCancelServiceReason] = useState("");
  const [cancelingService, setCancelingService] = useState(false);

  const isOrderDelivered = Array.isArray(serviceData?.services) &&
    serviceData.services.length > 0 &&
    serviceData.services.every((service) => Number(service?.status) === 7);

  const [showDeadlineModal, setShowDeadlineModal] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [selectedTimer, setSelectedTimer] = useState(null);
  const [quoteDeadlineNow, setQuoteDeadlineNow] = useState(moment());
  const hasAutoUpdatedRef = useRef(false);
  const lastAutoUpdatedOrderRef = useRef(null);

  const cancelOptions = [
    "Seleccioné otro proveedor",
    "El precio no se ajusta al presupuesto",
    "Tiempo de entrega no cumple con mi requerimiento",
    "No cumple con las especificaciones técnicas",
    "No cuenta con las certificaciones requeridas",
    "Condiciones de pago no aceptables",
    "Proyecto cancelado o pospuesto",
    "Cambio en el requerimiento",
    "Error en la solicitud u orden duplicada"
  ];

  const isCanceledOrder = Number(order?.status) === 9;

  const DeadlineCountdown = ({ deadline }) => {
    const renderCountdown = (hoursValue, minutesValue, secondsValue) => (
      <>
        <span className="deadline-countdown-group">{hoursValue}</span>
        <span className="deadline-countdown-separator">:</span>
        <span className="deadline-countdown-group">{minutesValue}</span>
        <span className="deadline-countdown-separator">:</span>
        <span className="deadline-countdown-group">{secondsValue}</span>
      </>
    );

    const end = parseQuoteDeadline(deadline);
    if (!end) {
      return null;
    }

    const diff = moment.duration(end.diff(quoteDeadlineNow));
    if (diff.asMilliseconds() <= 0) {
      return renderCountdown("00", "00", "00");
    }

    const hours = Math.floor(diff.asHours());
    const minutes = diff.minutes();
    const seconds = diff.seconds();
    const paddedHours = String(hours).padStart(2, "0");
    const paddedMinutes = String(minutes).padStart(2, "0");
    const paddedSeconds = String(seconds).padStart(2, "0");

    return renderCountdown(paddedHours, paddedMinutes, paddedSeconds);
  };

  const parseQuoteDeadline = (deadlineValue) => {
    if (!deadlineValue) return null;

    if (moment.isMoment(deadlineValue)) {
      return deadlineValue.isValid() ? deadlineValue.clone() : null;
    }

    const strictFormats = [
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DDTHH:mm:ssZ",
      "DD/MM/YYYY HH:mm:ss",
      "DD/MM/YYYY HH:mm",
      "DD-MM-YYYY HH:mm:ss",
      "DD-MM-YYYY HH:mm"
    ];

    const value = typeof deadlineValue === "string" ? deadlineValue.trim() : deadlineValue;
    let parsed = moment(value, strictFormats, true);

    if (parsed.isValid()) {
      return parsed;
    }

    parsed = moment(value);
    return parsed.isValid() ? parsed : null;
  };

  const getQuoteDeadline = (service) => {
    if (!service) return null;
    return (
      service.epno_deadline ||
      service.epnoDeadline ||
      service.quote_deadline ||
      service.deadline ||
      null
    );
  };

  const getQuoteCountdownTone = (service) => {
    const parsedDeadline = parseQuoteDeadline(getQuoteDeadline(service));
    if (!parsedDeadline) return "deadline-countdown-badge--normal";

    const remainingMs = parsedDeadline.diff(quoteDeadlineNow);
    const criticalThresholdMs = 5 * 60 * 60 * 1000;

    if (remainingMs <= criticalThresholdMs) {
      return "deadline-countdown-badge--critical";
    }

    const configuredWindowsHours = [24, 48, 120];
    const remainingHours = remainingMs / (60 * 60 * 1000);
    const selectedWindowHours =
      configuredWindowsHours.find((hours) => remainingHours <= hours) ||
      configuredWindowsHours[configuredWindowsHours.length - 1];
    const halfWindowMs = (selectedWindowHours / 2) * 60 * 60 * 1000;

    if (remainingMs <= halfWindowMs) {
      return "deadline-countdown-badge--half";
    }

    return "deadline-countdown-badge--normal";
  };

  const getQuoteCountdownVisual = (service) => {
    const toneClass = getQuoteCountdownTone(service);
    const toneLabelMap = {
      "deadline-countdown-badge--normal": "normal",
      "deadline-countdown-badge--half": "half",
      "deadline-countdown-badge--critical": "critical"
    };

    const styleMap = {
      "deadline-countdown-badge--normal": {
        background: "linear-gradient(135deg, #1f6dff 0%, #2f7dfd 100%)",
        boxShadow: "0 4px 10px rgba(15, 81, 151, 0.18)"
      },
      "deadline-countdown-badge--half": {
        background: "#efaa28",
        boxShadow: "0 4px 10px rgba(239, 170, 40, 0.32)"
      },
      "deadline-countdown-badge--critical": {
        background: "#b42d00",
        boxShadow: "0 4px 10px rgba(180, 45, 0, 0.34)"
      }
    };

    return {
      toneClass,
      toneLabel: toneLabelMap[toneClass] || "normal",
      style: styleMap[toneClass] || styleMap["deadline-countdown-badge--normal"]
    };
  };

  const hasValidQuoteDeadline = (service) => {
    const deadline = getQuoteDeadline(service);
    return !!parseQuoteDeadline(deadline);
  };

  const hasServicesWithExpiredQuoteDeadline = Array.isArray(serviceData?.services) &&
    serviceData.services.some((service) => {
      const currentStatus = Number(service?.status);
      const parsedDeadline = parseQuoteDeadline(getQuoteDeadline(service));

      if (currentStatus !== 2 || !parsedDeadline) {
        return false;
      }

      return parsedDeadline.isSameOrBefore(quoteDeadlineNow);
    });

  const hasCanceledServices = Array.isArray(serviceData?.services) &&
    serviceData.services.some((service) => Number(service?.status) === 9);

  const canReactivateOrder =
    isCanceledOrder ||
    hasServicesWithExpiredQuoteDeadline ||
    hasCanceledServices;
  const canReactivateByRole = [1, 4].includes(Number(role ?? effectiveRole));

  const sameEntityId = (left, right) => {
    if (left == null || right == null) return false;
    return String(left).trim() === String(right).trim();
  };

  const getServiceQuotes = (serviceId) => {
    if (!proposals) return [];

    const serviceIdText = serviceId != null ? String(serviceId) : "";
    const directQuotes = proposals?.[serviceId] || proposals?.[serviceIdText];
    if (Array.isArray(directQuotes)) {
      return directQuotes;
    }

    const allQuoteLists = Object.values(proposals || {}).filter(Array.isArray);
    if (!allQuoteLists.length) {
      return [];
    }

    return allQuoteLists.flat().filter((quote) => {
      const quoteServiceId = quote?.service_id ?? quote?.serviceId ?? quote?.service;
      return quoteServiceId != null && String(quoteServiceId) === serviceIdText;
    });
  };

  const isSupplierQuoteWindowOpen = (service) => {
    if (!service || Number(service.status) !== 2) {
      return false;
    }

    const deadline = getQuoteDeadline(service);
    if (!deadline) {
      return true;
    }

    const parsedDeadline = parseQuoteDeadline(deadline);
    if (!parsedDeadline) {
      return true;
    }

    return parsedDeadline.isAfter(quoteDeadlineNow);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setQuoteDeadlineNow(moment());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const cancelIndividualService = async (serviceId, reason) => {
    if (!reason.trim()) {
      message.warning("Por favor selecciona un motivo de cancelación.");
      return;
    }

    setCancelingService(true);

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const cancelData = {
        id: serviceId,
        reason: reason,
        clientId: role === 4 ? user.id : undefined,
        supplierId: selectedServiceToCancel?.supplier_id || undefined
      };
      if (role === 6 && !cancelData.supplierId) {
        message.warning("No se encontró información del proveedor.");
        setCancelingService(false);
        return;
      }

      const resp = await ServicesService.CancelService(cancelData);

      if (resp.data.success) {
        message.success("Servicio cancelado correctamente");
        setReload(!reload);
        setCancelServiceModal(false);
        setSelectedServiceToCancel(null);
        setCancelServiceReason("");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        const errorMsg = resp.data.message || "Error al cancelar el servicio";
        message.error(errorMsg !== "undefined" ? errorMsg : "Error al cancelar el servicio");
      }
    } catch (error) {
      const errorMsg = error.message || "Hubo un error al cancelar el servicio";
      message.error(errorMsg !== "undefined" ? errorMsg : "Hubo un error al cancelar el servicio");
    } finally {
      setCancelingService(false);
    }
  };

  const openCancelServiceModal = (service) => {
    setSelectedServiceToCancel(service);
    setCancelServiceModal(true);
  };
  const enableProposalEdit = (proposal, uploadIndex) => {
    setEditingProposal(proposal.id);
    setEditProposalValues({
      title: proposal.title || '',
      description: proposal.description || '',
      price: proposal.price || 0,
      epno_price: proposal.epno_price || 0,
      due_date: proposal.due_date || '',
      place: uploadIndex + 1,
      change_type: proposal.change_type || ''
    });
  };
  const saveProposalEdit = async (proposalId, serviceId) => {
    setLoadingQuote(true);

    const updatedData = {
      title: editProposalValues.title,
      description: editProposalValues.description,
      price: editProposalValues.price,
      epno_price: editProposalValues.epno_price,
      due_date: editProposalValues.due_date,
      place: editProposalValues.place,
      change_type: editProposalValues.change_type
    };

    try {
      const resp = await ServicesService.UpdateQuote(proposalId, updatedData);

      if (resp.data.success) {
        message.success("Cotización actualizada.");
        setProposals(prev => ({
          ...prev,
          [serviceId]: prev[serviceId].map(prop =>
            prop.id === proposalId ? { ...prop, ...updatedData } : prop
          )
        }));
        if (editProposalValues.epno_price !== undefined) {
          setProposalPrice(prev => ({
            ...prev,
            [proposalId]: editProposalValues.epno_price
          }));
        }

        if (editProposalValues.place !== undefined) {
          setPositions(prev => ({
            ...prev,
            [proposalId]: editProposalValues.place
          }));
        }

        setEditingProposal(null);

      } else {
        message.error("Error al actualizar: " + resp.data.message);
      }
    } catch (error) {
      message.error("Hubo un error al actualizar la cotización.");
    }

    setLoadingQuote(false);
  };

  const cancelProposalEdit = () => {
    setEditingProposal(null);
    setEditProposalValues({
      title: '',
      description: '',
      price: 0,
      epno_price: 0,
      due_date: '',
      place: 0,
      change_type: ''
    });
  };

  useEffect(() => {
    const filterVisibleServices = () => {
      if ([1, 4, 3, 5, 6].includes(role)) {
        return serviceData.services;
      }

      return [];
    };

    const fetchQuotes = async (visibleServices) => {
      try {
        const response = await ServicesService.GetQuotesByOrder(order.orderId);
        if (!response.data.success) return {};

        let quotes = response.data.proposals || {};

        switch (role) {
          case 6:
            Object.keys(quotes).forEach(serviceId => {
              quotes[serviceId] = quotes[serviceId].filter(
                (q) => sameEntityId(q?.supplier_id ?? q?.supplierId ?? q?.user_id ?? q?.userId, userData?.id)
              );
            });
            break;
          case 4:
            Object.keys(quotes).forEach(serviceId => {
              const service = visibleServices.find(s => s.serviceId == serviceId);
              if (!service || service.status < 3) {
                delete quotes[serviceId];
              } else if (service.status >= 3 && service.supplier_id) {
                quotes[serviceId] = quotes[serviceId].filter(q => q.status === true);
              }
            });
            break;
          default:
            break;
        }

        return quotes;
      } catch (error) {
        return {};
      }
    };

    const fetchReviews = async (serviceIds) => {
      try {
        const reviewsData = {};
        await Promise.all(serviceIds.map(async (serviceId) => {
          try {
            const response = await ServicesService.GetReviewByService(serviceId);
            reviewsData[serviceId] = response.data.success ? response.data.reviews : [];
          } catch (error) {
            reviewsData[serviceId] = [];
          }
        }));
        return reviewsData;
      } catch (error) {
        return {};
      }
    };

    const fetchComplaints = async (serviceIds) => {
      try {
        const complaintsData = {};
        await Promise.all(serviceIds.map(async (serviceId) => {
          try {
            const response = await ServicesService.GetComplaintByService(serviceId);
            complaintsData[serviceId] = response.data.success ? response.data.complaints : [];
          } catch (error) {
            complaintsData[serviceId] = [];
          }
        }));
        return complaintsData;
      } catch (error) {
        return {};
      }
    };

    const autoUpdateExpiredServices = async () => {
      if (effectiveRole === 6) {
        return;
      }

      if (!order?.orderId || !Array.isArray(serviceData?.services)) {
        hasAutoUpdatedRef.current = true;
        return;
      }

      if (lastAutoUpdatedOrderRef.current !== order.orderId) {
        lastAutoUpdatedOrderRef.current = order.orderId;
        hasAutoUpdatedRef.current = false;
      }

      if (hasAutoUpdatedRef.current) {
        return;
      }

      const now = moment();
      const updates = [];

      for (const service of serviceData.services) {
        const quoteDeadline = getQuoteDeadline(service);
        const parsedDeadline = parseQuoteDeadline(quoteDeadline);
        if (
          service.status === 2 &&
          parsedDeadline &&
          parsedDeadline.isBefore(now)
        ) {
          updates.push(ServicesService.UpdateStatus(service.serviceId, 3));
        }
      }

      if (updates.length > 0) {
        try {
          await Promise.all(updates);
          message.info("Se actualizaron servicios con deadline vencido.");
          setReload(prev => !prev);
          hasAutoUpdatedRef.current = true;
        } catch (err) {
        }
      } else {
        hasAutoUpdatedRef.current = true;
      }
    };

    const fetchData = async () => {
      const visibleServices = filterVisibleServices();
      setFilteredServices(visibleServices);

      const serviceIds = visibleServices.map(s => s.serviceId);
      const [quotes, reviews, complaints] = await Promise.all([
        fetchQuotes(visibleServices),
        fetchReviews(serviceIds),
        fetchComplaints(serviceIds)
      ]);

      setProposals(quotes);
      const positionsMap = {};
      Object.values(quotes || {}).forEach((list) => {
        if (Array.isArray(list)) {
          list.forEach((proposal, idx) => {
            if (proposal && proposal.id != null) {
              positionsMap[proposal.id] = idx + 1;
            }
          });
        }
      });
      setPositions(prev => ({
        ...prev,
        ...positionsMap
      }));
      setReviews(reviews);
      setComplaints(complaints);
    };

    fetchData();
    autoUpdateExpiredServices();
  }, [order.orderId, role, userData.id, userRoleCat1, serviceData, effectiveRole, setReload]);

  const updateService = (service_id, status) => {
    setLoadServiceChangeStep(true);
    ServicesService.UpdateStatus(service_id, status).then(resp => {
      if (resp.data.success === true) {
        setReload(!reload);
        message.success("Actualización hecha correctamente").then(() => {
          window.location.reload();
        });
      } else {
        const errorMsg = resp.data.message;
        if (errorMsg && typeof errorMsg === 'string' && errorMsg.trim() !== '' && errorMsg !== 'undefined') {
          message.error(errorMsg);
        } else {
          message.error("Error al procesar la solicitud");
        }
      }
      setLoadServiceChangeStep(false);
    }).catch(e => {
      message.error("Hubo un error al enviar los datos.");
      setLoadServiceChangeStep(false);
    });
  };

  const hasCapturedTruePrice = (service) => {
    const currentValue = truePriceByService[service?.serviceId] ?? service?.true_price;
    return currentValue !== null && currentValue !== undefined && String(currentValue).trim() !== '';
  };

  const handleChangeToEnCamino = (service) => {
    if (hasCapturedTruePrice(service)) {
      updateService(service.serviceId, 6);
      return;
    }

    Modal.confirm({
      title: 'Atención',
      centered: true,
      width: 420,
      content: 'Antes de cambiar el estatus a En camino, recuerda capturar el valor de la orden de compra.',
      okText: 'Continuar',
      cancelText: 'Capturar valor',
      onOk: () => updateService(service.serviceId, 6)
    });
  };

  const editService = (service_id, body) => {
    setLoadServiceChangeStep(true);
    ServicesService.Update(service_id, body).then(resp => {
      if (resp.data.success === true) {
        setReload(!reload);
        message.success("Actualización hecha correctamente").then(() => {
          window.location.reload();
        });
      } else {
        const errorMsg = resp.data.message;
        if (errorMsg && typeof errorMsg === 'string' && errorMsg.trim() !== '' && errorMsg !== 'undefined') {
          message.error(errorMsg);
        } else {
          message.error("Error al procesar la solicitud");
        }
      }
      setLoadServiceChangeStep(false);
    }).catch(e => {
      message.error("Hubo un error al enviar los datos.");
      setLoadServiceChangeStep(false);
    });
  };

  const rejectOrder = (id, body) => {
    setLoadServiceChangeStep(true);

    OrderService.Update(id, body).then(resp => {
      if (resp.data.success === true) {
        const updatePromises = serviceData.services.map(service => {
          const serviceBody = {
            status: body.status,
            rejectData: body.rejectData
          };

          return ServicesService.Update(service.serviceId, serviceBody);
        });
        return Promise.all(updatePromises);
      } else {
        throw new Error(resp.data.message);
      }
    }).then(serviceResponses => {
      const allSuccess = serviceResponses.every(resp => resp.data.success === true);

      if (allSuccess) {
        const messageText = body.status === 8
          ? "La orden de compra ha sido rechazada"
          : "La orden de compra ha sido cancelada";

        message.success(messageText).then(() => {
          setReload(!reload);
          setTimeout(() => {
            window.location.reload();
          }, 500);
        });
      } else {
        const messageText = body.status === 8
          ? "Orden rechazada, pero algunos servicios no se actualizaron correctamente"
          : "Orden cancelada, pero algunos servicios no se actualizaron correctamente";

        message.warning(messageText).then(() => {
          setReload(!reload);
          setTimeout(() => {
            window.location.reload();
          }, 500);
        });
      }
    }).catch(e => {
      message.error("Hubo un error al procesar la solicitud");
    }).finally(() => {
      setLoadServiceChangeStep(false);
    });
  };

  const reactivateOrder = (id) => {
    if (!canReactivateByRole) {
      message.warning("No tienes permisos para reactivar esta orden.");
      return;
    }

    setLoadServiceChangeStep(true);

    OrderService.Update(id, { status: 1 }).then(resp => {
      if (resp.data.success === true) {
        const updatePromises = serviceData.services.map(service => {
          return ServicesService.Update(service.serviceId, {
            status: 1,
            rejectData: null
          });
        });

        return Promise.all(updatePromises);
      }

      throw new Error(resp.data.message);
    }).then(serviceResponses => {
      const allSuccess = serviceResponses.every(resp => resp.data.success === true);

      if (allSuccess) {
        message.success("La orden se reactivó y volvió a Revisión").then(() => {
          setReload(!reload);
          setTimeout(() => {
            window.location.reload();
          }, 500);
        });
      } else {
        message.warning("La orden se reactivó, pero algunos servicios no se actualizaron correctamente").then(() => {
          setReload(!reload);
          setTimeout(() => {
            window.location.reload();
          }, 500);
        });
      }
    }).catch(() => {
      message.error("Hubo un error al reactivar la orden");
    }).finally(() => {
      setLoadServiceChangeStep(false);
    });
  };

  const uploadIndustryPO = (service_id) => {
    ServicesService.Update(service_id, { client_po_file: industryPO }).then(resp => {
      if (resp.data.success === true) {
        message.success("Actualización de datos exitoso!", 0.5).then(() => {
          window.location.reload();
        });
      } else {
        const errorMsg = resp.data.message;
        if (errorMsg && typeof errorMsg === 'string' && errorMsg.trim() !== '' && errorMsg !== 'undefined') {
          message.error(errorMsg);
        } else {
          message.error("Error al procesar la solicitud");
        }
      }
    }).catch((error) => {
      message.error("Hubo un error al enviar los datos.");
    });
  };

  const handleUploadIndustryPO = (service) => {
    if (!service?.supplier_id) {
      message.warning("Primero debe aceptar una cotización para poder subir la orden de compra");
      return;
    }

    if (!industryPO) {
      message.warning("Seleccione su orden de compra antes de subirla");
      return;
    }

    uploadIndustryPO(service.serviceId);
  };

  const uploadSupplierPO = (service_id) => {
    ServicesService.Update(service_id, {
      epno_po_file: supplierPO,
      status: 4
    }).then(resp => {
      if (resp.data.success === true) {
        message.success("Actualizacion de datos exitoso!", 0.5).then(() => {
          window.location.reload();
        });
      } else {
        const errorMsg = resp.data.message;
        if (errorMsg && typeof errorMsg === 'string' && errorMsg.trim() !== '' && errorMsg !== 'undefined') {
          message.error(errorMsg);
        } else {
          message.error("Error al procesar la solicitud");
        }
      }
    }).catch((error) => {
      message.error("Hubo un error al enviar los datos.");
    });
  };

  const handleSaveTruePrice = (service) => {
    const serviceId = service?.serviceId;
    const currentStatus = Number(service?.status ?? order?.status);
    const currentRole = Number(role ?? effectiveRole);
    const selectedCurrency =
      truePriceCurrencyByService[serviceId] ??
      service?.change_type ??
      'MXN';

    if (![1, 5].includes(currentRole)) {
      message.warning("No tienes permisos para editar este valor.");
      return;
    }

    if (currentStatus >= 7) {
      message.warning("El valor de la orden de compra ya no se puede editar en estatus Entregado.");
      return;
    }

    if (!serviceId) {
      message.error("No se pudo identificar el servicio.");
      return;
    }

    const inputValue = truePriceByService[serviceId] ?? service?.true_price ?? '';
    const parsedValue = Number(String(inputValue).replace(/,/g, '').trim());

    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
      message.warning("Ingresa un valor valido para la orden de compra.");
      return;
    }

    if (!['USD', 'MXN', 'EUR'].includes(String(selectedCurrency).toUpperCase())) {
      message.warning("Selecciona un tipo de cambio valido.");
      return;
    }

    setSavingTruePriceByService((prev) => ({ ...prev, [serviceId]: true }));

    ServicesService.UpdateById(serviceId, {
      true_price: parsedValue,
      change_type: String(selectedCurrency).toUpperCase()
    })
      .then((resp) => {
        const backendMessage =
          typeof resp?.data?.message === 'string'
            ? resp.data.message
            : '';
        const isSuccessfulResponse =
          resp?.data?.success === true ||
          (resp?.status >= 200 && resp?.status < 300 && resp?.data?.success !== false) ||
          /updated successfully|actualizad[oa]/i.test(backendMessage);

        if (isSuccessfulResponse) {
          setTruePriceByService((prev) => ({ ...prev, [serviceId]: String(parsedValue) }));
          setTruePriceCurrencyByService((prev) => ({
            ...prev,
            [serviceId]: String(selectedCurrency).toUpperCase()
          }));
          setTruePriceLockedByService((prev) => ({ ...prev, [serviceId]: true }));
          setReload(!reload);
          message.open({
            type: 'success',
            content: 'Valor de orden de compra guardado correctamente.',
            icon: <CheckCircleFilled style={{ color: '#52c41a' }} />
          });
        } else {
          const errorMsg =
            typeof resp?.data?.message === 'string' && resp.data.message.trim() !== ''
              ? resp.data.message
              : 'Error al guardar el valor de la orden de compra.';
          message.error(errorMsg);
        }
      })
      .catch(() => {
        message.error("Hubo un error al guardar el valor de la orden de compra.");
      })
      .finally(() => {
        setSavingTruePriceByService((prev) => ({ ...prev, [serviceId]: false }));
      });
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result;
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const propsPOIndustry = {
    beforeUpload: async (file) => {
      const base64 = await getBase64(file);
      setIndustryPO(base64);
      return false;
    },
    application
  };

  const propsPOSupplier = {
    beforeUpload: async (file) => {
      const base64 = await getBase64(file);
      setSupplierPO(base64);
      return false;
    },
    application
  };

  const handleInputChange = (e) => {
    setNewQuote({ ...newQuote, [e.target.name]: e.target.value });
  };

  const handleDateChange = (date, dateString) => {
    setNewQuote({ ...newQuote, due_date: dateString });
  };

  const handleFileUpload = async (file) => {
    const base64 = await getBase64(file);
    setNewQuote({ ...newQuote, proposal_file: base64 });
    return false;
  };

  const handleEpnoFileUpload = async (file) => {
    const base64 = await getBase64(file);
    setNewQuote({ ...newQuote, epno_file: base64 });
    return false;
  };

  const handleSubmit = async (service_id) => {
    if (loadingQuote) return;

    setLoadingQuote(true);

    const updatedQuote = {
      ...newQuote,
      supplier_bussiness: newQuote.supplier_name,
      service_id: service_id,
    };

    try {
      const resp = await ServicesService.NewInternalQuote(updatedQuote);
      if (resp.data.success) {
        message.success("Cotización guardada exitosamente");
        setReload(!reload);
        window.location.reload();
      } else {
        message.error("Error al guardar la cotización: " + resp.data.message);
      }
    } catch (error) {
      message.error("Hubo un error al enviar la cotización.");
    }

    setLoadingQuote(false);
  };

  const handleInputSupplierChange = (e) => {
    setNewSupplierQuote({ ...newSupplierQuote, [e.target.name]: e.target.value });
  };

  const handleDateSupplierChange = (date, dateString) => {
    setNewSupplierQuote({ ...newSupplierQuote, due_date: dateString });
  };

  const handleSupplierFileUpload = async (file) => {
    const base64 = await getBase64(file);
    setNewSupplierQuote({ ...newSupplierQuote, proposal_file: base64 });
    return false;
  };

  const handleSupplierSubmit = async (serviceInput) => {
    if (loadingQuote) return;

    const resolvedServiceIdRaw =
      typeof serviceInput === "object"
        ? (serviceInput?.serviceId ?? serviceInput?.service_id ?? serviceInput?.id)
        : serviceInput;
    const resolvedServiceId = resolvedServiceIdRaw != null ? String(resolvedServiceIdRaw) : "";

    const selectedService =
      (typeof serviceInput === "object" && serviceInput) ||
      [...(filteredServices || []), ...((serviceData && serviceData.services) || [])].find(
        (service) => {
          const serviceIdValue = service?.serviceId ?? service?.service_id ?? service?.id;
          return serviceIdValue != null && String(serviceIdValue) === resolvedServiceId;
        }
      );

    if (!resolvedServiceId) {
      message.error("No se pudo identificar el servicio para guardar la cotización.");
      return;
    }

    if (!isSupplierQuoteWindowOpen(selectedService)) {
      message.warning("Solo puedes cotizar servicios en estatus 2 y antes del deadline.");
      return;
    }

    const existingQuotes = getServiceQuotes(resolvedServiceId);
    const alreadyQuoted = Array.isArray(existingQuotes)
      ? existingQuotes.some((quote) => sameEntityId(quote?.supplier_id ?? quote?.supplierId ?? quote?.user_id ?? quote?.userId, userData?.id))
      : false;

    if (alreadyQuoted) {
      message.warning("Ya existe una propuesta para este proveedor en este servicio.");
      return;
    }

    setLoadingQuote(true);

    const updatedQuote = {
      ...newSupplierQuote,
      service_id: resolvedServiceId,
    };

    try {
      const resp = await ServicesService.NewQuote(updatedQuote);

      if (resp.data.success) {
        message.success("Cotización guardada exitosamente");
        const user = JSON.parse(localStorage.getItem("user"));
        const orderId = updatedQuote.order_id || updatedQuote.orderId;

        if (orderId && user?.id) {
          await ChatService.NewQuote(resolvedServiceId, {
            orderId,
            supplierId: user.id,
          });
        }

        setReload(!reload);
        window.location.reload();
      } else {
        message.error("Error al guardar la cotización: " + resp.data.message);
      }
    } catch (error) {
      const backendMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string" ? error.response.data : "");
      message.error(backendMessage || "Hubo un error al enviar la cotización.");
    }

    setLoadingQuote(false);
  };

  const updateServiceProposal = async (proposalId) => {
    setLoadingQuote(true);

    const updatedData = editingProposal === proposalId
      ? {
        title: editProposalValues.title,
        description: editProposalValues.description,
        price: editProposalValues.price,
        epno_price: editProposalValues.epno_price,
        due_date: editProposalValues.due_date,
        place: editProposalValues.place,
        change_type: editProposalValues.change_type,
        epno_file: editProposalValues.epno_file || proposalFiles[proposalId]
      }
      : {
        epno_price: proposalPrice[proposalId] || 0,
        place: positions[proposalId] || 0,
        epno_file: proposalFiles[proposalId] || null
      };

    try {
      const resp = await ServicesService.UpdateQuote(proposalId, updatedData);
      if (resp.data.success) {
        message.success("Cotización actualizada correctamente").then(_ => {
          window.location.reload();
        });
        setReload(!reload);
        if (editingProposal === proposalId) {
          setEditingProposal(null);
        }
      } else {
        message.error("Error al actualizar: " + resp.data.message);
      }
    } catch (error) {
      message.error("Hubo un error al actualizar la cotización.");
    }

    setLoadingQuote(false);
  };

  const chooseServiceProposal = (proposal, serviceId) => {
    setLoadServiceChangeStep(true);

    const body = {
      proposal_id: proposal.id,
      supplier_id: proposal.supplier_id,
      supplier_name: proposal.supplier_name,
      supplier_bussiness: proposal.supplier_bussiness,
      supplier_fcm: proposal.supplier_fcm,
      supplier_due_date: proposal.due_date,
      supplier_price: proposal.price,
      epno_file: proposal.epno_file,
      epno_price: proposal.epno_price,
      change_type: proposal.change_type,
    };

    const requiredFields = [
      'proposal_id',
      'supplier_id',
      'supplier_name',
      'supplier_bussiness',
      'supplier_due_date',
      'supplier_price',
      'epno_file',
      'epno_price',
    ];

    const missingField = requiredFields.find(key => {
      const value = body[key];
      return value === undefined || value === null || value === '';
    });

    if (missingField) {
      const fieldName = missingField.replace(/_/g, ' ');

      message.error(`Error: El campo "${fieldName}" está vacío o inválido en la propuesta.`);
      setLoadServiceChangeStep(false);
      return;
    }

    ServicesService.ChooseQuote(proposal.id, body)
      .then((resp) => {
        if (resp.data.success === true) {
          const selectedKey = `selected_proposal_${serviceId}`;
          localStorage.setItem(selectedKey, proposal.id.toString());

          setReload(!reload);
          setLoadServiceChangeStep(false);
          message.success("Elección exitosa", 1).then(() => {
            window.location.reload();
          });
        } else {
          const errorMsg = resp.data.message;
          if (errorMsg && typeof errorMsg === 'string' && errorMsg.trim() !== '' && errorMsg !== 'undefined') {
            message.error(errorMsg);
          } else {
            message.error("Error al procesar la solicitud");
          }
          setLoadServiceChangeStep(false);
        }
      })
      .catch((error) => {
        message.error("Hubo un error al enviar los datos.");
        setLoadServiceChangeStep(false);
      });
  };

  return (
    <div className="new-order-info-container">
      <Row gutter={[12, 12]} justify="center" align="middle">

        <Col xs={24}>
          <Card className="order-header-card background-gris">
            <Row gutter={[12, 12]} align="middle">
              <Col xs={24}>
                <Row gutter={[12, 12]} align="middle">
                  <Col xs={12} md={8}>
                    <div className="order-header-info">
                      <div className="order-id-badge">
                        <span className="order-id-label">Número de REQ:</span>
                        <span className="order-id-value">{order.orderId}</span>
                      </div>
                      <div className="order-title-info">
                        <span className="order-title-label">Título:</span>
                        <span className="order-title-value">{order.title}</span>
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} md={8}>
                    <div className="order-status-info">
                      <span className="order-date-label">Fecha de creación:</span>
                      <span className="order-date-value">
                        {moment(order.created_at).format("DD/MM/YYYY HH:mm")}
                      </span>
                    </div>
                  </Col>
                  <Col xs={24} md={8} className="cancel-button-container">
                    <Row gutter={[8, 8]} justify="end">
                      {!isOrderDelivered && ((order.status <= 3 || order.status == 11) && (role == 5 || role == 1)) &&
                        <Col>
                          <Button
                            danger
                            icon={<WarningFilled />}
                            loading={loadServiceChangeStep}
                            onClick={() => {
                              setCancelType("reject");
                              setShowCancelModal(true);
                            }}
                          >
                            Rechazar Orden
                          </Button>
                        </Col>
                      }
                      {!isOrderDelivered && ((order.status <= 3 || order.status == 11) && (role == 4)) &&
                        <Col>
                          <Button
                            danger
                            loading={loadServiceChangeStep}
                            onClick={() => {
                              setCancelType("cancel");
                              setShowCancelModal(true);
                            }}
                          >
                            Cancelar Orden
                          </Button>
                        </Col>
                      }
                      {!isOrderDelivered && canReactivateOrder && canReactivateByRole &&
                        <Col>
                          <Popconfirm
                            title="¿Deseas reactivar esta orden?"
                            description="La orden y sus servicios volverán al estatus 1 (Revisión)."
                            onConfirm={() => reactivateOrder(order.orderId)}
                            okText="Sí, reactivar"
                            cancelText="No"
                          >
                            <Button
                              type="primary"
                              icon={<CheckCircleOutlined />}
                              loading={loadServiceChangeStep}
                            >
                              Reactivar Orden
                            </Button>
                          </Popconfirm>
                        </Col>
                      }
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col xs={24}>
                {(order.status == 8 || (order.status == 9 && order.rejectData && order.rejectData.trim() !== "")) && (
                  <div className="status-alert-card">
                    {order.status == 8 &&
                      <p className="status-message">
                        <b>La orden ha sido rechazada debido a: </b>
                        {order.rejectData}
                      </p>
                    }
                    {order.status == 9 && order.rejectData && order.rejectData.trim() !== "" &&
                      <p className="status-message">
                        <b>La orden ha sido cancelada debido a: </b>
                        {order.rejectData}
                      </p>
                    }
                  </div>
                )}
              </Col>
            </Row>
          </Card>
        </Col>

        {(canViewAssignedBuyerContact || canViewAssignedSellerContact) && (
          <Col xs={24}>
            <Card className="order-header-card background-gris buyer-contact-card">
              {canViewAssignedBuyerContact && (
                <div className="buyer-contact-mini-card" style={{ marginBottom: canViewAssignedSellerContact ? 20 : 0 }}>
                  <span className="buyer-contact-mini-title">Contacto del comprador asignado</span>
                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}>
                      <div className="buyer-contact-field">
                        <span className="buyer-contact-label">Nombre</span>
                        <span className="buyer-contact-value">{buyerContact.name}</span>
                      </div>
                    </Col>
                    <Col xs={24} md={12}>
                      <div className="buyer-contact-field">
                        <span className="buyer-contact-label">Email</span>
                        <span className="buyer-contact-value">
                          {buyerContact.email !== "No disponible" ? (
                            <a href={`mailto:${buyerContact.email}`}>{buyerContact.email}</a>
                          ) : (
                            buyerContact.email
                          )}
                        </span>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {canViewAssignedSellerContact && (
                <div className="buyer-contact-mini-card">
                  <span className="buyer-contact-mini-title">Contacto del vendedor asignado</span>
                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}>
                      <div className="buyer-contact-field">
                        <span className="buyer-contact-label">Nombre</span>
                        <span className="buyer-contact-value">{sellerContact.name}</span>
                      </div>
                    </Col>
                    <Col xs={24} md={12}>
                      <div className="buyer-contact-field">
                        <span className="buyer-contact-label">Email</span>
                        <span className="buyer-contact-value">
                          {sellerContact.email !== "No disponible" ? (
                            <a href={`mailto:${sellerContact.email}`}>{sellerContact.email}</a>
                          ) : (
                            sellerContact.email
                          )}
                        </span>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}
            </Card>
          </Col>
        )}

        <Col xs={24}>
          <Card
            className="services-main-card actions-back"
            title={<h3 className="services-title">Servicios</h3>}
          >
            <Row gutter={[12, 12]} align="middle" justify="center">
              <Col xs={24}>
                <div className="expected-delivery">
                  <b>Fecha de entrega esperada:</b>{" "}
                  <p>{moment(order.cliet_due_date).format("DD/MM/YYYY")}</p>
                </div>
                <div className="service-collapse">
                  {filteredServices.map((sub) => (
                    <Collapse bordered={false} className="background-gris service-item">
                      <Panel
                        key={sub.serviceId}
                        header={
                          <div className="service-header-content">
                            <div className="service-title-section">
                              <span className="service-id">{sub.serviceId}</span>
                              <span className="service-title">
                                {editingServiceId === sub.serviceId ? (
                                  <Input
                                    style={{ width: '400px', marginLeft: '8px' }}
                                    value={editValues.title}
                                    onChange={e =>
                                      setEditValues({ ...editValues, title: e.target.value })
                                    }
                                  />
                                ) : (
                                  <span> – {sub.title}</span>
                                )}
                              </span>
                            </div>
                            <div className="service-actions">
                              <ServiceStatus status={sub.status} />
                              {Number(sub.status) === 2 && hasValidQuoteDeadline(sub) && isSupplierQuoteWindowOpen(sub) && (
                                (() => {
                                  const countdownVisual = getQuoteCountdownVisual(sub);
                                  return (
                                    <>
                                      <div
                                        className={`deadline-countdown-badge ${countdownVisual.toneClass}`}
                                        style={countdownVisual.style}
                                      >
                                        <ClockCircleOutlined className="deadline-countdown-icon" />
                                        <span className="deadline-countdown-time">
                                          <DeadlineCountdown deadline={getQuoteDeadline(sub)} />
                                        </span>
                                        <span className="deadline-countdown-label">Restante</span>
                                      </div>
                                      {SHOW_QUOTE_TIMER_DEBUG && (
                                        <span className="deadline-countdown-debug">{countdownVisual.toneLabel}</span>
                                      )}
                                    </>
                                  );
                                })()
                              )}
                              {Number(sub.status) === 2 && hasValidQuoteDeadline(sub) && !isSupplierQuoteWindowOpen(sub) && (
                                <Tag color="red">El tiempo de cotización para este servicio se agoto.</Tag>
                              )}
                              {filteredServices.length > 1 &&
                                sub.status < 7 &&
                                sub.status !== 8 &&
                                sub.status !== 9 &&
                                (role === 1 || role === 4) && (
                                  <Button
                                    danger
                                    size="small"
                                    className="cancel-service-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openCancelServiceModal(sub);
                                    }}
                                  >
                                    Cancelar Servicio
                                  </Button>
                                )}
                            </div>
                          </div>
                        }
                      >
                        <Row gutter={[12, 12]} justify="center">
                          <Col xs={24}>
                            <Card className="service-details-card">
                              <Descriptions
                                bordered
                                size="small"
                                title={
                                  <div className="descriptions-title-container">
                                    <h4>Datos del servicio</h4>
                                    <div className="service-action-buttons">
                                      {(role == 5 && sub.status === 1) && (
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                          <Button
                                            type="primary"
                                            icon={<UploadOutlined />}
                                            loading={loadServiceChangeStep}
                                            onClick={() => {
                                              setSelectedServiceId(sub.serviceId);
                                              setShowDeadlineModal(true);
                                            }}
                                          >
                                            Verificar PO
                                          </Button>
                                          <Popconfirm
                                            title="¿Seguro que desea rechazar la orden de compra?"
                                            onConfirm={() => updateService(sub.serviceId, 8)}
                                            okText="Si"
                                            cancelText="No"
                                          >
                                            <Button variant="solid" danger icon={<WarningFilled />} loading={loadServiceChangeStep}>
                                              Rechazar PO
                                            </Button>
                                          </Popconfirm>
                                          <Popconfirm
                                            title="¿Seguro que desea enviar el servicio a corrección?"
                                            onConfirm={() => updateService(sub.serviceId, 10)}
                                            okText="Si"
                                            cancelText="No"
                                          >
                                            <Button type="primary" loading={loadServiceChangeStep}>
                                              Mandar a corrección
                                            </Button>
                                          </Popconfirm>
                                        </div>
                                      )}

                                      {((role == 1 || role == 5) && sub.status === 5) && (
                                        <Popconfirm
                                          title="¿Seguro que desea confirmar esta orden como lista?"
                                          onConfirm={() => handleChangeToEnCamino(sub)}
                                          okText="Si"
                                          cancelText="No"
                                        >
                                          <Button type="primary" icon={<CheckCircleOutlined />} loading={loadServiceChangeStep}>
                                            Orden Lista
                                          </Button>
                                        </Popconfirm>
                                      )}

                                      {(role == 1 || role == 3) && sub.status === 2 && (
                                        <Popconfirm
                                          title="¿Seguro que la orden esta lista para subir su cotización?"
                                          onConfirm={() => updateService(sub.serviceId, 3)}
                                          okText="Si"
                                          cancelText="No"
                                        >
                                          <Button
                                            type="primary"
                                            icon={<UploadOutlined />}
                                            disabled={getServiceQuotes(sub.serviceId).length === 0}
                                          >
                                            Subir COT
                                          </Button>
                                        </Popconfirm>
                                      )}

                                      {role == 4 && sub.status == 6 && (
                                        <Popconfirm
                                          title="¿Seguro que ha recibido su orden completa?"
                                          onConfirm={() => updateService(sub.serviceId, 7)}
                                          okText="Si"
                                          cancelText="No"
                                        >
                                          <Button type="primary" icon={<CheckCircleOutlined />} loading={loadServiceChangeStep}>
                                            Entregado
                                          </Button>
                                        </Popconfirm>
                                      )}

                                      {role == 4 && (sub.status === 1 || sub.status === 10) && editingServiceId !== sub.serviceId && (
                                        <Button
                                          type="primary"
                                          icon={<EditOutlined />}
                                          loading={loadServiceChangeStep}
                                          onClick={() => {
                                            setEditingServiceId(sub.serviceId);
                                            setEditValues({
                                              title: sub.title,
                                              description: sub.description,
                                              quantity: sub.quantity,
                                              service_file: sub.service_file
                                            });
                                          }}
                                        >
                                          Editar servicio
                                        </Button>
                                      )}

                                      {editingServiceId === sub.serviceId && (
                                        <Button
                                          type="primary"
                                          icon={<CheckCircleOutlined />}
                                          loading={loadServiceChangeStep}
                                          onClick={() => {
                                            const body = {
                                              title: editValues.title,
                                              description: editValues.description,
                                              quantity: editValues.quantity,
                                              service_file: editValues.service_file
                                            };
                                            if (sub.status === 10) {
                                              body.status = 1;
                                            }
                                            editService(sub.serviceId, body);
                                            setEditingServiceId(null);
                                          }}
                                        >
                                          Guardar cambios
                                        </Button>
                                      )}

                                      {((role == 6 || role == 1 || role == 3 || role == 5) && sub.status === 4) && (
                                        <Popconfirm
                                          title="¿Seguro que el servicio ha sido elaborado?"
                                          onConfirm={() => updateService(sub.serviceId, 5)}
                                          okText="Si"
                                          cancelText="No"
                                        >
                                          <Button type="primary" icon={<CheckCircleOutlined />} loading={loadServiceChangeStep}>
                                            Servicio elaborado
                                          </Button>
                                        </Popconfirm>
                                      )}
                                    </div>
                                  </div>
                                }
                                column={{ xl: 3, lg: 3, md: 3, sm: 1, xs: 1 }}
                              >
                                <Descriptions.Item label="Descripción" span={3}>
                                  {editingServiceId === sub.serviceId ? (
                                    <Input.TextArea
                                      value={editValues.description}
                                      onChange={(e) =>
                                        setEditValues({ ...editValues, description: e.target.value })
                                      }
                                      rows={2}
                                    />
                                  ) : (
                                    sub.description
                                  )}
                                </Descriptions.Item>
                                <Descriptions.Item label="Cantidad" span={3}>
                                  {editingServiceId === sub.serviceId ? (
                                    <Input
                                      type="number"
                                      value={editValues.quantity}
                                      onChange={(e) =>
                                        setEditValues({ ...editValues, quantity: e.target.value })
                                      }
                                    />
                                  ) : (
                                    sub.quantity
                                  )}
                                </Descriptions.Item>
                                <Descriptions.Item label="Categoría" span={3}>
                                  <CategoryTag sub={sub} vsCategories={vsCategories} />
                                </Descriptions.Item>
                                <Descriptions.Item label="Fecha de entrega del proveedor" span={3}>
                                  {sub.due_date || "Por definir"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Fecha de entrega final" span={3}>
                                  {sub.finish_date || "Por definir"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Archivo del servicio" span={3}>
                                  {editingServiceId === sub.serviceId ? (
                                    <Upload
                                      beforeUpload={async (file) => {
                                        const base64 = await getBase64(file);
                                        setEditValues({ ...editValues, service_file: base64 });
                                        return false;
                                      }}
                                      maxCount={1}
                                    >
                                      <Button icon={<UploadOutlined />}>
                                        Seleccionar archivo
                                      </Button>
                                    </Upload>
                                  ) : (
                                    <Row justify="center">
                                      <Col xs={24} md={12} style={{ textAlign: "center" }}>
                                        {sub.service_file ? (
                                          <a
                                            href={sub.service_file}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            download
                                          >
                                            <DownloadOutlined /> Especificaciones
                                          </a>
                                        ) : (
                                          <p type="secondary">No se encontró ningún archivo</p>
                                        )}
                                      </Col>
                                    </Row>
                                  )}
                                </Descriptions.Item>
                                {role == 1}
                                <Descriptions.Item label="Cotización del proveedor" span={3}>
                                  <Row gutter={[12, 12]} justify="center">
                                    <Col xs={24} md={12} style={{ textAlign: "center" }}>
                                      {sub.epno_file ?
                                        <a href={sub.epno_file} target="_blank" rel="noopener noreferrer" download>
                                          <DownloadOutlined />
                                          Especificaciones
                                        </a>
                                        : <p>Por definir</p>}
                                    </Col>
                                  </Row>
                                </Descriptions.Item>
                                {role != 6 && sub.status >= 3 && sub.status !== 8 &&
                                  <Descriptions.Item
                                    label={role == 4 ? "Orden de compra" : (role == 1 || role === 3 || role || 5) && "Orden de compra del cliente"}
                                    span={3}
                                  >
                                    <Row gutter={[12, 12]} justify="center">
                                      <Col xs={24} md={12} style={{ textAlign: "center" }}>
                                        {(role == 1 || role == 4) && sub.client_po_file == "" && sub.status !== 8 ? (
                                          <>
                                            {!sub.supplier_id ? (
                                              <Button
                                                style={{ width: 245, minWidth: 245, maxWidth: 245, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', whiteSpace: 'nowrap', paddingInline: 16 }}
                                                onClick={() => message.warning("Primero debe aceptar una cotización para poder subir la orden de compra")}
                                              >
                                                Seleccionar orden de compra
                                              </Button>
                                            ) : (
                                              <Upload {...propsPOIndustry}>
                                                <Button style={{ width: 245, minWidth: 245, maxWidth: 245, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', whiteSpace: 'nowrap', paddingInline: 16 }}>Seleccionar orden de compra</Button>
                                              </Upload>
                                            )}
                                            <Button
                                              style={{ width: 245, minWidth: 245, maxWidth: 245, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                              type="primary"
                                              onClick={() => { handleUploadIndustryPO(sub) }}
                                            >
                                              Subir orden de compra
                                            </Button>
                                          </>
                                        ) : sub.client_po_file ? (
                                          <a href={sub.client_po_file} target="_blank" rel="noopener noreferrer" download>
                                            <DownloadOutlined /> {role == 4 ? "Orden de compra" : (role == 1 || role === 3 || role || 5) && "Orden de compra del cliente"}
                                          </a>
                                        ) : <p>Por definir</p>}
                                      </Col>
                                    </Row>
                                  </Descriptions.Item>
                                }
                                {role != 4 && sub.status !== 8 &&
                                  <Descriptions.Item
                                    label={role === 6 ? 'Orden de compra' : (role == 1 || role === 3 || role || 5) && "Orden de compra proveedor"}
                                    span={3}
                                  >
                                    <Row gutter={[12, 12]} justify="center">
                                      <Col xs={24} md={12} style={{ textAlign: "center" }}>
                                        {((role == 1 || role == 3) && sub.client_po_file != "" && sub.status == 3) ? (
                                          <>
                                            <Upload {...propsPOSupplier}>
                                              <Button style={{ width: 245, minWidth: 245, maxWidth: 245, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', whiteSpace: 'nowrap', paddingInline: 16 }}>Seleccionar orden de compra</Button>
                                            </Upload>
                                            <Button
                                              style={{ width: 245, minWidth: 245, maxWidth: 245, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                              type="primary"
                                              disabled={supplierPO == ''}
                                              onClick={() => { uploadSupplierPO(sub.serviceId) }}
                                            >
                                              Subir orden de compra
                                            </Button>
                                          </>
                                        ) : sub.epno_po_file != '' ? (
                                          <a href={sub.epno_po_file} target="_blank" rel="noopener noreferrer" download>
                                            <DownloadOutlined /> {role === 6 ? 'Orden de compra' : (role == 1 || role === 3 || role || 5) && "Orden de compra proveedor"}
                                          </a>
                                        ) : <p>Por definir</p>}
                                      </Col>
                                    </Row>
                                  </Descriptions.Item>
                                }

                                <Descriptions.Item label="Estatus" span={3}>
                                  {role == 1 ? (
                                    <p>
                                      {OrderLogs.adminStatusLogs.find(step => step.status === Number(sub.status ?? order.status))?.text || 'Estatus no disponible'}
                                      {([8, 9, 11].includes(Number(sub.status ?? order.status))) &&
                                        (sub.rejectData || order.rejectData) &&
                                        (sub.rejectData || order.rejectData).trim() !== "" &&
                                        ` - ${sub.rejectData || order.rejectData}`
                                      }
                                    </p>
                                  ) : role == 3 ? (
                                    <p>
                                      {OrderLogs.buyerStatusLogs.find(step => step.status === Number(sub.status ?? order.status))?.text || 'Estatus no disponible'}
                                      {([8, 9, 11].includes(Number(sub.status ?? order.status))) &&
                                        (sub.rejectData || order.rejectData) &&
                                        (sub.rejectData || order.rejectData).trim() !== "" &&
                                        ` - ${sub.rejectData || order.rejectData}`
                                      }
                                    </p>
                                  ) : role == 4 ? (
                                    <p>
                                      {OrderLogs.clientStatusLogs.find(step => step.status === Number(sub.status ?? order.status))?.text || 'Estatus no disponible'}
                                      {([8, 9, 11].includes(Number(sub.status ?? order.status))) &&
                                        (sub.rejectData || order.rejectData) &&
                                        (sub.rejectData || order.rejectData).trim() !== "" &&
                                        ` - ${sub.rejectData || order.rejectData}`
                                      }
                                    </p>
                                  ) : role == 5 ? (
                                    <p>
                                      {OrderLogs.vendorStatusLogs.find(step => step.status === Number(sub.status ?? order.status))?.text || 'Estatus no disponible'}
                                      {([8, 9, 11].includes(Number(sub.status ?? order.status))) &&
                                        (sub.rejectData || order.rejectData) &&
                                        (sub.rejectData || order.rejectData).trim() !== "" &&
                                        ` - ${sub.rejectData || order.rejectData}`
                                      }
                                    </p>
                                  ) : role == 6 ? (
                                    <p>
                                      {OrderLogs.supplierStatusLogs.find(step => step.status === Number(sub.status ?? order.status))?.text || 'Estatus no disponible'}
                                      {([9, 11].includes(Number(sub.status ?? order.status))) &&
                                        (sub.rejectData || order.rejectData) &&
                                        (sub.rejectData || order.rejectData).trim() !== "" &&
                                        ` - ${sub.rejectData || order.rejectData}`
                                      }
                                    </p>
                                  ) : 'Estatus no disponible'}
                                </Descriptions.Item>

                                {[1, 5].includes(Number(role ?? effectiveRole)) && [4, 5, 6, 7].includes(Number(sub.status ?? order.status)) && (
                                  <Descriptions.Item label="Valor orden de compra" span={3}>
                                    {(() => {
                                      const status = Number(sub.status ?? order.status);
                                      const isDelivered = status >= 7;
                                      const hasStoredValue = sub.true_price !== null && sub.true_price !== undefined && String(sub.true_price).trim() !== '';
                                      const isLocked = isDelivered || (truePriceLockedByService[sub.serviceId] ?? hasStoredValue);

                                      return (
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: '100%' }}>
                                      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                                        <Input
                                          placeholder="Valor de la orden de compra"
                                          style={{ width: '100%' }}
                                          disabled={isLocked}
                                          value={truePriceByService[sub.serviceId] ?? (sub.true_price ?? '')}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            setTruePriceByService((prev) => ({
                                              ...prev,
                                              [sub.serviceId]: value
                                            }));
                                          }}
                                        />
                                      </div>
                                      <div style={{ flex: '0 1 130px', minWidth: 120 }}>
                                        <Select
                                          style={{ width: '100%' }}
                                          disabled={isLocked}
                                          value={truePriceCurrencyByService[sub.serviceId] ?? sub?.change_type ?? 'MXN'}
                                          onChange={(value) => {
                                            setTruePriceCurrencyByService((prev) => ({
                                              ...prev,
                                              [sub.serviceId]: value
                                            }));
                                          }}
                                          options={[
                                            { value: 'USD', label: 'USD' },
                                            { value: 'MXN', label: 'MXN' },
                                            { value: 'EUR', label: 'EUR' }
                                          ]}
                                        />
                                      </div>
                                      <div style={{ flex: '0 1 180px', minWidth: 140 }}>
                                        <Button
                                          type="primary"
                                          style={{ width: '100%' }}
                                          loading={!!savingTruePriceByService[sub.serviceId]}
                                          disabled={isDelivered}
                                          onClick={() => {
                                            if (isLocked) {
                                              setTruePriceLockedByService((prev) => ({
                                                ...prev,
                                                [sub.serviceId]: false
                                              }));
                                              return;
                                            }
                                            handleSaveTruePrice(sub);
                                          }}
                                        >
                                          {isLocked ? 'Editar valor' : 'Guardar valor'}
                                        </Button>
                                      </div>
                                    </div>
                                      );
                                    })()}
                                  </Descriptions.Item>
                                )}

                                {/* Mostrar reseñas: rol 4 siempre puede ver, otros roles solo si hay reseñas */}
                                {sub.status === 7 && (role === 4 || (reviews[sub.serviceId] && reviews[sub.serviceId].length > 0)) && (
                                  <Descriptions.Item label="Calificación y Comentarios" span={3}>
                                    <ServiceReviews
                                      serviceId={sub.serviceId}
                                      userRole={role}
                                      reviews={reviews}
                                      setReviews={setReviews}
                                      userData={userData}
                                    />
                                  </Descriptions.Item>
                                )}
                                {/* Mostrar quejas: cliente cuando está entregado o ya tiene quejas; otros roles solo si hay quejas */}
                                {((role === 4 && (sub.status === 7 || (complaints[sub.serviceId] && complaints[sub.serviceId].length > 0))) ||
                                  (role !== 4 && complaints[sub.serviceId] && complaints[sub.serviceId].length > 0)) && (
                                    <Descriptions.Item label="Quejas del Servicio" span={3}>
                                      <ServiceComplaints
                                        serviceId={sub.serviceId}
                                        userRole={role}
                                        complaints={complaints}
                                        setComplaints={setComplaints}
                                        userData={userData}
                                        status={sub.status}
                                      />
                                    </Descriptions.Item>
                                  )}
                              </Descriptions>
                            </Card>
                          </Col>
                        </Row>
                        <Row gutter={[12, 12]} justify="center">
                          <Col xs={24}>
                            <div className="quotes-section">
                              <h4 className="quotes-header">Cotizaciones</h4>
                              {getServiceQuotes(sub.serviceId).length > 0 ? (
                                <Collapse bordered={false} className="quotes-collapse background-gris">
                                  {getServiceQuotes(sub.serviceId).map((pro, index) => (
                                    <Panel
                                      key={index}
                                      header={
                                        <Row gutter={[6, 12]}>
                                          <Col xs={24} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                              <b>{role === 4 ? `Propuesta ${index + 1}` : pro.supplier_bussiness}</b>
                                              {[1, 3, 5].includes(Number(role)) && !!sub.client_po_file && sameEntityId(sub.supplier_id ?? sub.supplierId, pro.supplier_id ?? pro.supplierId) && (
                                                <Tag color="green" style={{ fontSize: '12px' }}>Cotización seleccionada</Tag>
                                              )}
                                            </div>
                                            {(role == 1 || role == 3) && sub.status === 2 && editingProposal !== pro.id && (
                                              <Button
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  enableProposalEdit(pro, index);
                                                }}
                                              >
                                                Editar
                                              </Button>
                                            )}

                                          </Col>
                                        </Row>
                                      }
                                    >
                                      <Card className="quote-details-card">
                                        <h3>Datos de la cotización</h3>
                                        <Descriptions bordered size="small">
                                          {role !== 4 && (
                                            <Descriptions.Item label="Proveedor" span={3}>
                                              {pro.supplier_bussiness}
                                            </Descriptions.Item>
                                          )}

                                          <Descriptions.Item label="Título" span={3}>
                                            {editingProposal === pro.id ? (
                                              <Input
                                                value={editProposalValues.title}
                                                onChange={e => setEditProposalValues({ ...editProposalValues, title: e.target.value })}
                                                placeholder="Título de la cotización"
                                              />
                                            ) : (
                                              pro.title
                                            )}
                                          </Descriptions.Item>

                                          <Descriptions.Item label="Descripción" span={3}>
                                            {editingProposal === pro.id ? (
                                              <Input.TextArea
                                                value={editProposalValues.description}
                                                onChange={e => setEditProposalValues({ ...editProposalValues, description: e.target.value })}
                                                rows={3}
                                                placeholder="Descripción de la cotización"
                                              />
                                            ) : (
                                              pro.description
                                            )}

                                          </Descriptions.Item>
                                          {role !== 4 && (
                                            <Descriptions.Item label="Precio del proveedor" span={3}>
                                              {editingProposal === pro.id ? (
                                                <Input
                                                  type="number"
                                                  value={editProposalValues.price}
                                                  onChange={e => setEditProposalValues({ ...editProposalValues, price: e.target.value })}
                                                  placeholder="Precio del proveedor"
                                                  addonBefore="$"
                                                />
                                              ) : (
                                                pro.price ? `$${pro.price} ${pro.change_type}` : "Por definir"
                                              )}
                                            </Descriptions.Item>
                                          )}

                                          {role !== 6 && (
                                            <Descriptions.Item label="Precio de EP&O" span={3}>
                                              {editingProposal === pro.id ? (
                                                <Input
                                                  type="number"
                                                  value={editProposalValues.epno_price}
                                                  onChange={e => setEditProposalValues({ ...editProposalValues, epno_price: e.target.value })}
                                                  placeholder="Precio de EP&O"
                                                  addonBefore="$"
                                                />
                                              ) : (role == 1 || role == 3) && sub.status == 2 ? (
                                                <Input
                                                  type="number"
                                                  name="epno_price"
                                                  value={proposalPrice[pro.id] || pro.epno_price || 0}
                                                  onChange={(e) => setProposalPrice({ ...proposalPrice, [pro.id]: e.target.value })}
                                                  placeholder="Precio de EP&O"
                                                  addonBefore="$"
                                                />
                                              ) : (
                                                <p>{pro.epno_price ? '$' + pro.epno_price : 'Por definir'}</p>
                                              )}
                                            </Descriptions.Item>
                                          )}

                                          <Descriptions.Item label="Tipo de cambio" span={3}>
                                            {editingProposal === pro.id ? (
                                              <Select
                                                style={{ width: '100%' }}
                                                value={editProposalValues.change_type}
                                                onChange={value => setEditProposalValues({ ...editProposalValues, change_type: value })}
                                                placeholder="Selecciona tipo de cambio"
                                              >
                                                <Select.Option value="MXN">MXN</Select.Option>
                                                <Select.Option value="USD">USD</Select.Option>
                                                <Select.Option value="EUR">EUR</Select.Option>
                                              </Select>
                                            ) : (
                                              <p>{pro.change_type || 'Por definir'}</p>
                                            )}
                                          </Descriptions.Item>

                                          <Descriptions.Item label="Posición" span={3}>
                                            <p>{index + 1}</p>
                                          </Descriptions.Item>

                                          <Descriptions.Item label="Fecha de entrega" span={3}>
                                            {editingProposal === pro.id ? (
                                              <DatePicker
                                                style={{ width: '100%' }}
                                                value={editProposalValues.due_date ? moment(editProposalValues.due_date) : null}
                                                onChange={(date) => setEditProposalValues({ ...editProposalValues, due_date: date ? date.format('YYYY-MM-DD') : '' })}
                                                placeholder="Fecha de entrega"
                                              />
                                            ) : (
                                              pro.due_date ? moment(pro.due_date).format("DD/MM/YYYY") : "Por definir"
                                            )}
                                          </Descriptions.Item>

                                          {pro.proposal_file && role !== 4 && (
                                            <Descriptions.Item label="Cotización" span={3}>
                                              <a href={pro.proposal_file} target="_blank" rel="noopener noreferrer" download>
                                                <DownloadOutlined /> Descargar Cotización
                                              </a>
                                            </Descriptions.Item>
                                          )}

                                          {role !== 6 && (
                                            <Descriptions.Item label="Cotización de EP&O" span={3}>
                                              {editingProposal === pro.id ? (
                                                <Upload
                                                  beforeUpload={async (file) => {
                                                    const base64 = await getBase64(file);
                                                    setEditProposalValues({ ...editProposalValues, epno_file: base64 });
                                                    return false;
                                                  }}
                                                  maxCount={1}
                                                >
                                                  <Button icon={<UploadOutlined />}>Subir archivo de EP&O</Button>
                                                </Upload>
                                              ) : (role == 1 || role == 3) && sub.status == 2 && !pro.epno_file ? (
                                                <Upload
                                                  beforeUpload={async (file) => {
                                                    const base64 = await getBase64(file);
                                                    setProposalFiles({ ...proposalFiles, [pro.id]: base64 });
                                                    return false;
                                                  }}
                                                  maxCount={1}
                                                >
                                                  <Button icon={<UploadOutlined />}>Subir archivo</Button>
                                                </Upload>
                                              ) : pro.epno_file ? (
                                                <>
                                                  {(role == 1 || role == 3) && sub.status == 2 &&
                                                    <Upload
                                                      beforeUpload={async (file) => {
                                                        const base64 = await getBase64(file);
                                                        setProposalFiles({ ...proposalFiles, [pro.id]: base64 });
                                                        return false;
                                                      }}
                                                      maxCount={1}
                                                    >
                                                      <Button icon={<UploadOutlined />}>Subir archivo</Button>
                                                    </Upload>
                                                  }
                                                  <a href={pro.epno_file} target="_blank" rel="noopener noreferrer" download>
                                                    <DownloadOutlined /> Descargar Cotización de EP&O
                                                  </a>
                                                </>
                                              ) : (
                                                <p>Por definir</p>
                                              )}
                                            </Descriptions.Item>
                                          )}

                                          {editingProposal === pro.id ? (
                                            <Descriptions.Item span={3}>
                                              <div style={{ display: 'flex', gap: '8px' }}>
                                                <Button
                                                  type="primary"
                                                  icon={<SaveOutlined />}
                                                  onClick={() => saveProposalEdit(pro.id, sub.serviceId)}
                                                  loading={loadingQuote}
                                                >
                                                  Guardar cambios
                                                </Button>
                                                <Button
                                                  icon={<CloseOutlined />}
                                                  onClick={cancelProposalEdit}
                                                >
                                                  Cancelar
                                                </Button>
                                              </div>
                                            </Descriptions.Item>
                                          ) : (
                                            ((role === 1 || role === 3) && sub.status == 2) && (
                                              <Descriptions.Item span={3}>
                                                <Button
                                                  type="primary"
                                                  disabled={!proposalPrice[pro.id] || !proposalFiles[pro.id]}
                                                  loading={loadingQuote}
                                                  onClick={() => updateServiceProposal(pro.id)}
                                                >
                                                  {loadingQuote ? "Guardando..." : "Guardar cambios"}
                                                </Button>
                                              </Descriptions.Item>
                                            )
                                          )}

                                          {((role === 1 || role === 4) && sub.status == 3 && !sub.supplier_id) &&
                                            <Descriptions.Item span={3}>
                                              <Button
                                                type="primary"
                                                loading={loadingQuote}
                                                onClick={() => chooseServiceProposal(pro, sub.serviceId)}
                                              >
                                                {loadingQuote ? "Guardando..." : "Aceptar cotización"}
                                              </Button>
                                            </Descriptions.Item>
                                          }
                                        </Descriptions>
                                      </Card>
                                    </Panel>
                                  ))}
                                </Collapse>
                              ) : (
                                <div className="no-quotes">
                                  <p>No hay cotizaciones para este servicio.</p>
                                </div>
                              )}

                              {((role == 1 || role == 3) && sub.status == 2) &&
                                <Collapse bordered={false} className="add-quote-collapse background-gris" expandIcon={({ isActive }) => <PlusOutlined rotate={isActive ? 45 : 0} />}>
                                  <Panel
                                    header={
                                      <Row gutter={[6, 12]}>
                                        <Col xs={24} style={{ display: 'flex', alignItems: 'center' }}>
                                          <b>Agregar una cotización externa</b>
                                        </Col>
                                      </Row>
                                    }
                                    key={sub.serviceId}
                                  >
                                    <Row gutter={[12, 12]} justify="center">
                                      <Col xs={24}>
                                        <Card className="quote-form-card">
                                          <Descriptions bordered size="small" column={{ xl: 3, lg: 3, md: 3, sm: 1, xs: 1 }}>
                                            <Descriptions.Item label="Proveedor" span={3}>
                                              <Input name="supplier_name" value={newQuote.supplier_name} onChange={handleInputChange} placeholder="Nombre del proveedor" />
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Título" span={3}>
                                              <Input name="title" value={newQuote.title} onChange={handleInputChange} placeholder="Título de la cotización" />
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Descripción" span={3}>
                                              <Input.TextArea name="description" value={newQuote.description} onChange={handleInputChange} placeholder="Descripción de la cotización" />
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Precio del proveedor" span={3}>
                                              <Input type="number" name="price" value={newQuote.price} onChange={handleInputChange} placeholder="Precio del proveedor" addonBefore="$" />
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Precio de EP&O" span={3}>
                                              <Input type="number" name="epno_price" value={newQuote.epno_price} onChange={handleInputChange} placeholder="Precio de EP&O" addonBefore="$" />
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Tipo de cambio" span={3}>
                                              <Select
                                                style={{ width: '100%' }}
                                                value={newQuote.change_type}
                                                onChange={(value) => setNewQuote({ ...newQuote, change_type: value })}
                                                placeholder="Selecciona tipo de cambio"
                                              >
                                                <Select.Option value="MXN">MXN</Select.Option>
                                                <Select.Option value="USD">USD</Select.Option>
                                                <Select.Option value="EUR">EUR</Select.Option>
                                              </Select>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Fecha de entrega" span={3}>
                                              <DatePicker style={{ width: "100%" }} onChange={handleDateChange} placeholder="Selecciona fecha de entrega" />
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Cotización del proveedor" span={3}>
                                              <Upload beforeUpload={handleFileUpload} maxCount={1}>
                                                <Button icon={<UploadOutlined />}>Subir archivo de cotización</Button>
                                              </Upload>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Cotización de EP&O" span={3}>
                                              <Upload beforeUpload={handleEpnoFileUpload} maxCount={1}>
                                                <Button icon={<UploadOutlined />}>Subir archivo de EP&O</Button>
                                              </Upload>
                                            </Descriptions.Item>
                                          </Descriptions>
                                          <Row justify="center" style={{ marginTop: "12px" }}>
                                            <Button
                                              type="primary"
                                              disabled={loadingQuote || !newQuote.supplier_name || !newQuote.title || !newQuote.description || !newQuote.price || !newQuote.due_date || !newQuote.proposal_file || !newQuote.epno_file || !newQuote.epno_price}
                                              onClick={() => handleSubmit(sub.serviceId)}
                                              loading={loadingQuote}
                                            >
                                              {loadingQuote ? "Enviando..." : "Guardar cotización externa"}
                                            </Button>
                                          </Row>
                                        </Card>
                                      </Col>
                                    </Row>
                                  </Panel>
                                </Collapse>
                              }

                              {role == 6 && isSupplierQuoteWindowOpen(sub) && (
                                <Collapse bordered={false} className="add-quote-collapse background-gris" expandIcon={({ isActive }) => <PlusOutlined rotate={isActive ? 45 : 0} />}>
                                  {filteredServices.map((subs) => {
                                    const serviceQuotes = getServiceQuotes(subs.serviceId);
                                    const isServiceQuoted = Array.isArray(serviceQuotes)
                                      ? serviceQuotes.some(
                                        (quote) => sameEntityId(quote?.supplier_id ?? quote?.supplierId ?? quote?.user_id ?? quote?.userId, userData?.id)
                                      )
                                      : false;
                                    if (!isServiceQuoted && sub.serviceId == subs.serviceId && isSupplierQuoteWindowOpen(subs)) {
                                      return (
                                        <Panel
                                          header={
                                            <Row gutter={[6, 12]}>
                                              <Col xs={24} style={{ display: 'flex', alignItems: 'center' }}>
                                                <b>Cotizar servicio {sub.serviceId}</b>
                                              </Col>
                                            </Row>
                                          }
                                          key={`add-quote-panel-${sub.serviceId}`}
                                        >
                                          <Row gutter={[12, 12]} justify="center">
                                            <Col xs={24}>
                                              <Card className="quote-form-card">
                                                <Descriptions bordered size="small" column={{ xl: 3, lg: 3, md: 3, sm: 1, xs: 1 }}>
                                                  <Descriptions.Item label="Título" span={3}>
                                                    <Input name="title" value={newSupplierQuote.title} onChange={handleInputSupplierChange} />
                                                  </Descriptions.Item>
                                                  <Descriptions.Item label="Descripción" span={3}>
                                                    <Input.TextArea name="description" value={newSupplierQuote.description} onChange={handleInputSupplierChange} />
                                                  </Descriptions.Item>
                                                  <Descriptions.Item label="Precio" span={3}>
                                                    <Input type="number" name="price" value={newSupplierQuote.price} onChange={handleInputSupplierChange} />
                                                  </Descriptions.Item>
                                                  <Descriptions.Item label="Tipo de cambio" span={3}>
                                                    <Select
                                                      style={{ width: '100%' }}
                                                      value={newSupplierQuote.change_type}
                                                      onChange={(value) => setNewSupplierQuote({ ...newSupplierQuote, change_type: value })}
                                                      placeholder="Selecciona tipo de cambio"
                                                    >
                                                      <Select.Option value="MXN">MXN</Select.Option>
                                                      <Select.Option value="USD">USD</Select.Option>
                                                      <Select.Option value="EUR">EUR</Select.Option>
                                                    </Select>
                                                  </Descriptions.Item>
                                                  <Descriptions.Item label="Fecha de entrega" span={3}>
                                                    <DatePicker style={{ width: "100%" }} onChange={handleDateSupplierChange} />
                                                  </Descriptions.Item>
                                                  <Descriptions.Item label="Cotización" span={3}>
                                                    <Upload beforeUpload={handleSupplierFileUpload} maxCount={1}>
                                                      <Button icon={<UploadOutlined />}>Subir archivo</Button>
                                                    </Upload>
                                                  </Descriptions.Item>
                                                </Descriptions>
                                                <Row justify="center" style={{ marginTop: "12px" }}>
                                                  <Button
                                                    type="primary"
                                                    disabled={
                                                      loadingQuote ||
                                                      !newSupplierQuote.supplier_name ||
                                                      !newSupplierQuote.title ||
                                                      !newSupplierQuote.description ||
                                                      !newSupplierQuote.price ||
                                                      !newSupplierQuote.change_type ||
                                                      !newSupplierQuote.due_date ||
                                                      !newSupplierQuote.proposal_file
                                                    }
                                                    onClick={() => handleSupplierSubmit(sub)}
                                                    loading={loadingQuote}
                                                  >
                                                    {loadingQuote ? "Enviando..." : "Guardar cotización"}
                                                  </Button>
                                                </Row>
                                              </Card>
                                            </Col>
                                          </Row>
                                        </Panel>
                                      );
                                    }
                                    return null;
                                  })}
                                </Collapse>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Panel>
                    </Collapse>
                  ))}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Modal
        title={cancelType === "reject" ? "Rechazar Orden" : "Cancelar Orden"}
        open={showCancelModal}
        onOk={() => {
          if (!cancelReason.trim()) {
            message.warning("Por favor selecciona un motivo.");
            return;
          }

          let statusToSet;
          if (cancelType === "reject") {
            statusToSet = 8;
          } else {
            statusToSet = 9;
          }

          rejectOrder(order.orderId, {
            status: statusToSet,
            rejectData: cancelReason
          });
          setShowCancelModal(false);
          setCancelReason("");
          setCancelType("");
        }}
        onCancel={() => {
          setShowCancelModal(false);
          setCancelReason("");
          setCancelType("");
        }}
        okText={cancelType === "reject" ? "Confirmar Rechazo" : "Confirmar Cancelación"}
        cancelText="Cerrar"
        okButtonProps={{
          danger: true,
          icon: cancelType === "reject" ? <WarningFilled /> : null
        }}
      >
        <p>
          {cancelType === "reject"
            ? "Estás a punto de rechazar esta orden de compra. Por favor selecciona el motivo:"
            : "Estás a punto de cancelar esta orden de compra. Por favor selecciona el motivo:"
          }
        </p>
        <Select
          style={{ width: '100%' }}
          placeholder="Selecciona un motivo"
          value={cancelReason}
          onChange={(value) => setCancelReason(value)}
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {cancelOptions.map((option, index) => (
            <Select.Option key={index} value={option}>
              {option}
            </Select.Option>
          ))}
        </Select>
        <div style={{ marginTop: '10px', color: '#666', fontSize: '12px' }}>
          {cancelType === "reject"
            ? "Nota: Esta acción rechazará la orden y el cliente deberá corregirla."
            : "Nota: Esta acción cancelará permanentemente la orden."
          }
        </div>
      </Modal>

      <Modal
        title={`Cancelar Servicio ${selectedServiceToCancel?.serviceId}`}
        open={cancelServiceModal}
        onOk={() => cancelIndividualService(selectedServiceToCancel?.serviceId, cancelServiceReason)}
        onCancel={() => {
          setCancelServiceModal(false);
          setSelectedServiceToCancel(null);
          setCancelServiceReason("");
        }}
        okText="Confirmar cancelación"
        cancelText="Cerrar"
        confirmLoading={cancelingService}
        okButtonProps={{ danger: true }}
      >
        <p>Estás a punto de cancelar el servicio: <b>{selectedServiceToCancel?.title}</b></p>
        <p>Por favor indica el motivo de la cancelación:</p>
        <Input.TextArea
          rows={4}
          value={cancelServiceReason}
          onChange={(e) => setCancelServiceReason(e.target.value)}
          placeholder="Motivo de la cancelación..."
        />
      </Modal>

      <Modal
        title="Confirmar envío a cotización"
        open={showDeadlineModal}
        onOk={() => {
          if (!selectedTimer) {
            message.warning("Selecciona un periodo.");
            return;
          }

          const deadline = moment().add(selectedTimer, 'hours').format('YYYY-MM-DD HH:mm:ss');
          ServicesService.Update(selectedServiceId, {
            status: 2,
            epno_deadline: deadline
          }).then(resp => {
            if (resp.data.success) {
              message.success("Servicio verificado y fecha límite asignada.");
              window.location.reload();
            } else {
              const errorMsg = resp.data.message;
              if (errorMsg && typeof errorMsg === 'string' && errorMsg.trim() !== '' && errorMsg !== 'undefined') {
                message.error(errorMsg);
              } else {
                message.error("Error al procesar la solicitud");
              }
            }
          }).catch(() => {
            message.error("Error al actualizar el servicio.");
          });

          setShowDeadlineModal(false);
          setSelectedTimer(null);
          setSelectedServiceId(null);
        }}
        onCancel={() => {
          setShowDeadlineModal(false);
          setSelectedTimer(null);
          setSelectedServiceId(null);
        }}
        okText="Confirmar"
        cancelText="Cancelar"
      >
        <p>¿Estás seguro que deseas enviar este servicio a cotización?</p>
        <p>Selecciona el periodo máximo de búsqueda de proveedor:</p>
        <Select
          style={{ width: '100%' }}
          placeholder="Selecciona un periodo"
          onChange={(value) => setSelectedTimer(value)}
        >
          <Select.Option value={24}>24 horas</Select.Option>
          <Select.Option value={48}>48 horas</Select.Option>
          <Select.Option value={120}>5 días</Select.Option>
        </Select>
      </Modal>
    </div>
  );
}
